<template>
  <div>
    <div
      :class="[loaded && !post ? 'py-16 px-6 lg:p-16' : '']"
      class="flex flex-col flex-wrap lg:flex-nowrap"
    >
      <div v-if="loaded" class="w-full">
        <div class="space-y-8 flex flex-col">
          <!-- OPTIONS BLOCK -->
          <div
            class="w-full lg:flex lg:space-x-2 justify-start lg:justify-between"
          >
            <div
              v-if="!video_preview && !file_preview && upload_options"
              class="flex space-x-1 items-center relative lg:py-0"
            >
              <button
                v-if="!file_preview_change && !thinking"
                :disabled="thinking"
                :class="[
                  thinking ? 'cursor-not-allowed' : '',
                  isContributor ? 'btn-cw-700' : 'btn-primary thinking-primary',
                ]"
                type="submit"
                class="mb-0 h-12 w-40 btn relative"
                @click="file_change(1)"
              >
                <div
                  class="absolute inset-0 w-full flex justify-center items-center z-20"
                >
                  <lottie v-if="thinking" :options="loadingAnimationOptions" />
                </div>
                <span :class="[thinking ? 'invisible' : '']">Upload File</span>
              </button>
              <form ref="selectFileForm">
                <input
                  ref="selectFile"
                  type="file"
                  class="hidden"
                  @input="file_selected"
                />
              </form>
              <button
                v-if="!file_preview_change && !image_files_only"
                :disabled="thinking"
                :class="[
                  thinking ? 'cursor-not-allowed bg-black hover:bg-black' : '',
                ]"
                type="submit"
                class="mb-0 h-12 w-40 btn btn-black relative"
                @click="file_change(2)"
              >
                <div
                  class="absolute inset-0 w-full flex justify-center items-center z-20"
                >
                  <lottie v-if="thinking" :options="loadingAnimationOptions" />
                </div>
                <span :class="[thinking ? 'invisible' : '']">Add Video</span>
              </button>
            </div>
            <div
              v-if="!video_preview && !file_preview"
              class="w-full flex space-x-1 lg:justify-end py-2 lg:py-0"
            >
              <!-- Search input -->
              <div
                class="h-12 w-48 rounded relative bg-white shadow border border-truegray-300"
              >
                <h6 :class="inputLabelClass">Search for resource</h6>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 absolute right-2 bottom-1/2 transform translate-y-1/2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z"
                  />
                </svg>
                <input
                  id="search_resources"
                  v-model="search_resources"
                  type="text"
                  name="search_resources"
                  autocomplete="search_resources"
                  :class="inputTextClass"
                />
              </div>
              <button
                type="button"
                :disabled="submit_keywords_button_disabled"
                :class="[
                  submit_keywords_button_disabled
                    ? 'opacity-25 cursor-not-allowed btn-trans-black'
                    : 'btn-black',
                  thinkingResourceSearch ? 'btn-black cursor-not-allowed' : '',
                ]"
                class="btn m-0 relative h-12"
                @click="searchKeywords()"
              >
                <div
                  class="absolute inset-0 w-full flex justify-center items-center z-20"
                >
                  <lottie
                    v-if="thinkingResourceSearch"
                    :options="loadingAnimationOptions"
                  />
                </div>
                <span :class="[thinkingResourceSearch ? 'invisible' : '']"
                  >Search</span
                >
              </button>
            </div>
            <!-- OPTIONS BLOCK -->

            <div v-if="file_abort_error" class="text-red-700">
              Please select the correct file format...
            </div>

            <!-- PREVIEW AND TITLE -->
            <div
              v-if="video_preview"
              class="relative"
              :class="show_video_preview ? 'w-min-content' : 'w-full'"
            >
              <div class="flex flex-col w-full">
                <div v-if="show_video_preview" class="relative">
                  <h3
                    class="z-10 absolute bottom-1/2 right-1/2 transform translate-x-1/2 translate-y-1/2"
                  >
                    Loading ...
                  </h3>
                  <iframe
                    ref="resourceVideoPreview"
                    type="iframe"
                    aspect="16by9"
                    class="inset-0 w-full h-full rounded relative z-30"
                    :src="show_video_preview"
                    frameborder="0"
                    style="height: 300px; width: 409px"
                  ></iframe>
                </div>
                <div v-else class="flex flex-col w-full mb-8">
                  <h3 class="mb-6">Select your platform</h3>
                  <div class="flex mb-4 relative">
                    <button
                      type="button"
                      class="inline-flex items-center cursor-pointer mr-4 text-p"
                      @click="video_type = 'YouTube'"
                    >
                      <span
                        :class="
                          video_type === 'YouTube'
                            ? 'font-semibold text-expo_red'
                            : ''
                        "
                        class="ml-1"
                        >YouTube</span
                      >
                    </button>
                    <div
                      class="w-px relative top-0.5 h-4 bg-truegray-300 mr-4"
                    ></div>
                    <button
                      type="button"
                      class="inline-flex items-center cursor-pointer mr-4 text-p"
                      @click="video_type = 'Vimeo'"
                    >
                      <span
                        :class="
                          video_type === 'Vimeo'
                            ? 'font-semibold text-cyan-400'
                            : ''
                        "
                        class="ml-1"
                        >Vimeo</span
                      >
                    </button>
                    <div
                      :class="[
                        video_type === 'YouTube'
                          ? 'bg-expo_red translate-x-0'
                          : 'bg-cyan-400 translate-x-130',
                      ]"
                      class="absolute h-1 left-1 top-full transform translate-y-2 w-16 transition-all duration-300 ease-in-out rounded"
                    ></div>
                  </div>
                </div>

                <div v-if="!show_video_preview">
                  <div
                    class="h-12 w-1/2 rounded relative bg-white shadow border border-truegray-300"
                  >
                    <h6
                      :class="inputLabelClass"
                      class="flex items-center group"
                    >
                      Video code
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3 ml-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <div :class="infoClass" class="lg:w-80">
                        Only insert the {{ video_type }} video ID number or
                        code. For eg
                        <span
                          v-if="video_type === 'Vimeo'"
                          class="text-truegray-500 italic"
                        >
                          https://vimeo.com/<span class="text-expo_red"
                            >108799588</span
                          >
                        </span>
                        <span v-else class="text-truegray-300 italic">
                          https://www.youtube.com/watch?v=<span
                            class="text-expo_red"
                            >GCHLt0NDkdY</span
                          >
                        </span>
                      </div>
                    </h6>
                    <input
                      id="post_link_title"
                      v-model="video_code"
                      type="text"
                      name="post_link_title"
                      autocomplete="post_link_title"
                      :class="inputTextClass"
                    />
                  </div>
                </div>

                <!-- FILE INPUT TILE -->
                <div
                  v-if="show_video_preview"
                  :style="{ width: '409px' }"
                  class="h-12 rounded relative bg-white shadow border border-truegray-300 mt-4"
                >
                  <h6 :class="inputLabelClass" class="flex items-center group">
                    File name
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3 ml-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <div :class="infoClass">Give this video a title...</div>
                  </h6>
                  <input
                    id="post_link_title"
                    v-model="file_title"
                    type="text"
                    name="post_link_title"
                    autocomplete="post_link_title"
                    :class="inputTextClass"
                  />
                </div>

                <div class="w-full flex space-x-1 mt-4">
                  <button
                    v-if="!show_video_preview"
                    type="button"
                    class="py-4 px-6 rounded-md font-primary text-sm leading-4 font-medium text-white hover:bg-black transition-all duration-100 ease-in-out"
                    :class="isContributor ? 'bg-cw-700' : 'bg-primary'"
                    @click="preview_video"
                  >
                    Preview
                  </button>
                  <button
                    v-if="show_video_preview"
                    type="button"
                    class="py-4 px-6 rounded-md font-primary text-sm leading-4 font-medium text-white hover:bg-black bg-primary transition-all duration-100 ease-in-out"
                    @click="save_video"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    class="py-4 px-6 rounded-md font-primary text-sm leading-4 font-medium hover:text-red-700 text-red-500 transition-all duration-100 ease-in-out"
                    @click="reset"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <!-- PREVIEW AND TITLE -->
            <div v-if="file_preview" class="relative">
              <h3>
                Upload <span v-if="file_preview_type === 1">a File</span
                ><span v-if="file_preview_type === 2">an Image</span>
              </h3>
              <div class="flex flex-col lg:flex-row">
                <div
                  v-if="file_preview_type === 2"
                  class="w-full lg:w-1/3 pr-8 lg:border-r lg:border-truegray-300"
                >
                  <div
                    class="bg-gray-500 w-full h-44 my-2 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20"
                    :style="{ backgroundImage: `url(${image_preview})` }"
                  >
                    <span class="hidden">{{ settings.profile_title }}</span
                    ><!-- Required ... spoof the dom -->
                  </div>
                </div>
                <div
                  class="w-full lg:w-2/3"
                  :class="file_preview_type === 2 ? 'pl-0 lg:pl-8' : ''"
                >
                  <h4 class="font-primary">Title:</h4>
                  <div class="flex items-center">
                    <!-- FILE TYPE ICON -->
                    <svg
                      v-if="file_preview_type === 1"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6 mr-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <!-- FILE INPUT TILE -->

                    <div
                      class="h-12 rounded relative bg-white shadow border border-truegray-300"
                    >
                      <h6
                        :class="inputLabelClass"
                        class="flex items-center group"
                      >
                        File name
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-3 w-3 ml-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        <div :class="infoClass">Give this file a title...</div>
                      </h6>
                      <input
                        id="post_link_title"
                        v-model="file_title"
                        type="text"
                        name="post_link_title"
                        autocomplete="post_link_title"
                        class="flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-72 h-12 font-primary text-black focus:placeholder-truegray-300"
                      />
                    </div>
                  </div>
                  <div class="flex justify-start space-x-6 items-center mt-6">
                    <!-- SAVE / CANCEL BUTTONS -->
                    <button
                      type="button"
                      class="h-12 m-0 btn"
                      :class="isContributor ? 'btn-cw-700' : 'btn-primary'"
                      @click="save_file"
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      class="text-red-600 m-0 underline bg-transparent border-none font-primary"
                      @click="reset"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- // RESOURCE Searched by keywords options -->
        <p v-if="resources.length === 0 && search_resources_active">
          We could not find resources.
        </p>
        <button
          v-if="search_resources_active"
          type="button"
          class="flex-none justify-center mb-4 underline font-primary cursor-pointer hover:text-primary"
          @click="resetKeywords()"
        >
          Reset Search
        </button>
        <!-- // RESOURCE Searched by keywords options -->

        <!-- // RESOURCE LIST  -->
        <div
          v-if="
            loaded && resources.length > 0 && !video_preview && !file_preview
          "
          class="mt-4 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6 gap-4"
        >
          <div
            v-for="(resource, i) in resource_search"
            v-show="resource_search_list"
            :key="i"
            class="cursor-pointer rounded flex flex-col justify-center transition-all duration-100 group"
            @click="handleResourceClick(resource)"
          >
            <div
              class="w-full overflow-hidden resource-card flex items-center justify-center aspect-w-1 aspect-h-1 border border-truegray-300 rounded-2xl bg-cover bg-center bg-no-repeat text-white bg-truegray-300 group-hover:bg-truegray-500 transition-all duration-300 ease-in-out relative"
              :style="
                resource.type === 2
                  ? { backgroundImage: 'url(' + resource.file_url + ')' }
                  : {}
              "
            >
              <div class="flex items-center justify-center w-full h-full">
                <svg
                  v-if="resource.type === 1"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-16 w-16 group-hover:opacity-0 transition-all duration-300 ease-in-out"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <svg
                  v-if="resource.type === 3"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-16 w-16 group-hover:opacity-0 transition-all duration-300 ease-in-out"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div
                  class="rounded absolute px-6 py-4 right-1/2 bottom-1/2 z-20 transform translate-x-1/2 translate-y-1/2 text-white border border-white font-primary opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out"
                >
                  <span v-if="post">Attach</span>
                  <span v-else>Edit</span>
                </div>
              </div>
            </div>
            <h6
              class="text-truegray-500 group-hover:text-black truncate ml-2 my-2"
            >
              {{ resource.title }}
            </h6>
          </div>
          <div
            v-for="(resource, i) in resources"
            v-show="!resource_search_list"
            :key="i"
            class="cursor-pointer rounded flex flex-col justify-center transition-all duration-100 group"
            @click="handleResourceClick(resource)"
          >
            <div
              class="w-full overflow-hidden resource-card flex items-center justify-center aspect-w-1 aspect-h-1 border border-truegray-300 rounded-2xl bg-cover bg-center bg-no-repeat text-white bg-truegray-300 group-hover:bg-truegray-500 transition-all duration-300 ease-in-out"
              :style="
                resource.type === 2
                  ? { backgroundImage: 'url(' + resource.file_url + ')' }
                  : {}
              "
            >
              <div class="flex items-center justify-center w-full h-full">
                <svg
                  v-if="resource.type === 1"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-16 w-16 group-hover:opacity-0 transition-all duration-300 ease-in-out"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <svg
                  v-if="resource.type === 3"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-16 w-16 group-hover:opacity-0 transition-all duration-300 ease-in-out"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div
                  class="rounded absolute px-6 py-4 right-1/2 bottom-1/2 z-20 transform translate-x-1/2 translate-y-1/2 text-white border border-white font-primary opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out"
                >
                  <span v-if="post">Attach</span>
                  <span v-else>Edit</span>
                </div>
              </div>
            </div>
            <h6
              class="text-truegray-500 group-hover:text-black truncate ml-2 my-2"
            >
              {{ resource.title }}
            </h6>
          </div>
        </div>

        <p
          v-if="
            loaded &&
            resources.length === 0 &&
            !video_preview &&
            !file_preview &&
            !search_resources_active
          "
          class="ml-2 mt-4"
        >
          Add some resources to get started.
        </p>

        <!-- PAGINATION -->
        <div
          v-if="
            (pagination.previouspage || pagination.nextpage) && !video_preview
          "
          class="flex justify-between items-center h-10"
        >
          <span
            :disabled="!pagination.previouspage"
            :class="[
              !pagination.previouspage
                ? 'opacity-25 cursor-not-allowed'
                : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
            ]"
            class="text-gray-500"
            @click="getList(pagination.previouspage)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 17l-5-5m0 0l5-5m-5 5h12"
              />
            </svg>
          </span>
          <span class="text-center text-xs"
            >Page {{ pagination.current_page }} of
            {{ pagination.total_pages }}</span
          >
          <span
            :disabled="!pagination.nextpage"
            :class="[
              !pagination.nextpage
                ? 'opacity-25 cursor-not-allowed'
                : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
            ]"
            class="text-gray-500"
            @click="getList(pagination.nextpage)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="!loaded"
      class="w-full rounded-xl flex flex-col items-center p-16"
    >
      <div class="mt-16 w-32 text-center">
        <lottie :options="loadingAnimationDarkOptions" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { shared } from '../../js/mixins/shared.js'
import { EventBus } from '../../js/plugins/event-bus.js'
import * as loadingAnimationDark from '../../shared/components/loading-dark.json'
import * as loadingAnimation from '../../shared/components/loading.json'

export default {
  mixins: [shared],

  props: {
    post: { default: false },
    types: { default: false },
    upload_options: { default: true },
    image_files_only: { default: false },
    // tiptap editor
    editorResources: { type: Boolean, default: false },
    cloud: { default: false },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimationDarkOptions: {
        animationData: loadingAnimationDark.default,
      },

      loadingAnimate: false,

      loaded: false,

      thinking: false,

      infoClass:
        'absolute left-full top-0 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white',
      inputTextClass:
        'flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-truegray-700',

      resources: [],

      resource_search_list: false,
      resource_search: [],

      // Add a new resource file...
      file_title: null,
      file_preview: null,
      file_preview_type: 0,
      file_preview_change: false,
      file_abort_error: false,

      video_type: 'YouTube',

      video_preview: false,
      video_code: null, // model
      show_video_preview: null,

      // Pagination
      pagination: {
        current_page: null,
        total_pages: null,
        per_page: 9,
        next_page: null,
        previous_page: null,
        last_page_url: null,
        next_page_url: null,
        prev_page_url: null,
        path: null,
        nextpage: null,
        previouspage: null,
      },

      search_resources: '',
      search_resources_active: false,
      thinkingResourceSearch: false,
      submit_keywords_button_disabled: true,
      image_preview: null,
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      isContributor: 'shared/isContributor',
      // filterResourceBy: 'shared/getFilterResourceBy',
    }),
  },

  watch: {
    video_type: {
      handler: function () {
        this.video_url_format(this.video_code)
      },

      deep: true,
    },

    video_code: {
      handler: function (val) {
        this.video_url_format(val)
      },

      deep: true,
    },

    search_resources: {
      handler: function (keywords) {
        if (keywords.length > 0) {
          this.$set(this, 'submit_keywords_button_disabled', false)
        } else {
          this.$set(this, 'submit_keywords_button_disabled', true)
        }
      },

      deep: true,
    },
  },

  mounted() {
    this.getList()
  },

  methods: {
    ...mapActions({
      dispatchGetResources: 'contributors/dispatchGetResources',
      dispatchSaveFile: 'contributors/dispatchSaveFile',
      dispatchSaveVideo: 'contributors/dispatchSaveVideo',
    }),

    async save_video() {
      // confirm('saving new resource')
      this.$set(this, 'thinking', true)
      let submitData = new FormData()
      submitData.append('video_code', this.video_code)
      submitData.append('video_title', this.file_title)
      submitData.append('video_type', this.video_type)
      this.reset()
      if (this.isContributor) {
        const response = await this.dispatchSaveVideo(submitData)
        this.$set(this, 'thinking', false)
        if (response.success) {
          this.getList()
        }

        if (response.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }
      } else {
        axios
          .post('/api/auth/add-video-resource', submitData)
          .then((response) => {
            this.$set(this, 'thinking', false)
            let result = response.data
            if (result.success) {
              this.$set(this, 'resource_search', [])
              this.$set(this, 'resource_search_list', false)
              this.getList()
            }
            if (response.data.alert) {
              EventBus.$emit('triggerAlertResponse', response.data.alert)
            }
          })
      }
    },

    preview_video() {
      if (!this.video_code) {
        return
      }

      let src = null
      if (this.video_type === 'YouTube') {
        src = 'https://www.youtube.com/embed/' + this.video_code
      }
      if (this.video_type === 'Vimeo') {
        src = 'https://player.vimeo.com/video/' + this.video_code
      }
      if (!src) {
        return
      }

      this.show_video_preview = src
    },

    video_url_format(code) {
      if (!code) {
        return
      }

      if (this.video_type === 'YouTube') {
        let str = code.replace('//www.youtube.com/embed/', '')
        this.resourceVideoEmbedUrl = '//www.youtube.com/embed/' + str
        code = str
      }

      if (this.video_type === 'Vimeo') {
        let str = code.replace('//player.vimeo.com/video/', '')
        this.resourceVideoEmbedUrl = '//player.vimeo.com/video/' + str
        code = str
      }

      return code
    },

    goToResource(id = 0) {
      this.$router.push({ path: '/manager/resource/' + id })
    },

    reset() {
      this.$set(this, 'file_preview_change', false)
      this.$set(this, 'file_preview', null)
      this.$set(this, 'file_title', null)
      this.$set(this, 'file_preview_type', 0)
      this.$set(this, 'file_abort_error', false)

      this.$set(this, 'video_type', 'YouTube')
      this.$set(this, 'video_preview', false)
      this.$set(this, 'video_code', null)
      this.$set(this, 'show_video_preview', null)

      this.$set(this, 'resource_search', [])
      this.$set(this, 'resource_search_list', false)
    },

    file_change(type = 1) {
      if (type === 1) {
        this.$refs.selectFile.click()
        this.$set(this, 'video_preview', false)
      }
      if (type === 2) {
        this.reset()
        this.$set(this, 'video_preview', true)
      }
    },

    file_selected() {
      let input = this.$refs.selectFile
      let file = input.files

      // Determine if we have the correct file format and categorise it's type for upload...
      let document_formats =
        /(\.doc|\.docx|\.odt|\.pdf|\.tex|\.txt|\.rtf|\.wps|\.wks|\.wpd)$/i
      let image_formats = /(\.png|\.jpg|\.jpeg)$/i
      let type = 0
      if (document_formats.exec(file[0].name)) {
        type = 1
      }
      if (image_formats.exec(file[0].name)) {
        type = 2
      }
      if (type < 1) {
        this.$set(this, 'file_abort_error', true)
        return
      }

      // Check to see if we are not restricting one format option on this file?
      if (this.image_files_only && type !== 2) {
        this.$set(this, 'file_abort_error', true)
        return
      }

      this.$set(this, 'file_abort_error', false)

      if (file && file[0]) {
        // Kick out if this is an image file and is not valid:
        if (!this.validateImageFormat(file[0]) && type === 2) {
          return
        }

        if (file[0].size > 1024 * 1024 * 2 && type === 1) {
          EventBus.$emit('triggerAlertResponse', {
            status: 'error',
            message: 'File size too large. 2mb file size limit exceeded.',
          })
          return
        }

        let reader = new FileReader()
        reader.onload = (e) => {
          this.$set(this, 'file_preview_change', true)
          this.$set(this, 'file_preview', file[0])
          this.$set(this, 'file_preview_type', type)
          this.$set(
            this,
            'file_title',
            file[0].name.split('.').slice(0, -1).join('.'),
          )
          this.$set(this, 'image_preview', e.target.result)
        }
        reader.readAsDataURL(file[0])
        this.$emit('input', file[0])
      }
    },

    async save_file() {
      this.$set(this, 'thinking', true)
      let submitData = new FormData()
      submitData.append('resource', this.file_preview)
      submitData.append('resource_title', this.file_title)
      submitData.append('resource_type', this.file_preview_type)
      this.reset()
      if (this.isContributor) {
        const response = await this.dispatchSaveFile(submitData)
        this.$set(this, 'thinking', false)
        if (response.success) {
          this.getList()
        }
        if (response.alert) {
          EventBus.$emit('triggerAlertResponse', response.alert)
        }
      } else {
        axios.post('/api/auth/add-resource', submitData).then((response) => {
          this.$set(this, 'thinking', false)
          const result = response.data
          if (result.success) {
            this.$set(this, 'resource_search', [])
            this.$set(this, 'resource_search_list', false)
            this.getList()
          }
          if (result.alert) {
            EventBus.$emit('triggerAlertResponse', result.alert)
          }
        })
      }
    },

    /**
        ============================ GET POSTS WITH FILTER OPTION ===============
    */

    searchKeywords() {
      this.$set(this, 'search_resources_active', true)
      this.getList()
    },

    resetKeywords() {
      this.$set(this, 'search_resources_active', false)
      this.$set(this, 'search_resources', '') // Reset the input field.
      this.getList()
    },

    async getList(gotopage = null) {
      const handleSuccess = (data) => {
        this.$set(this, 'loaded', true)
        let result = data.data
        this.$set(this, 'resource_search', [])
        this.$set(this, 'resource_search_list', false)

        this.$set(this, 'resources', result.resources.data)
        this.$set(this, 'loaded', true)
        EventBus.$emit('resources_ready')

        // pagination.total = total # of posts. / by per_page, + 1. Pagination doesnt show when not needed so +1 is never wrong on single page
        let total_pages =
          Math.floor(
            data.data.pagination.total / data.data.pagination.per_page,
          ) + 1
        this.$set(
          this.pagination,
          'current_page',
          data.data.pagination.current_page,
        )
        this.$set(this.pagination, 'total_pages', total_pages)
        this.$set(this.pagination, 'per_page', data.data.pagination.per_page)
        this.$set(
          this.pagination,
          'last_page_url',
          data.data.pagination.last_page_url,
        )
        this.$set(
          this.pagination,
          'next_page_url',
          data.data.pagination.next_page_url,
        )
        this.$set(
          this.pagination,
          'prev_page_url',
          data.data.pagination.prev_page_url,
        )
        this.$set(this.pagination, 'path', data.data.pagination.next_page_path)

        let prev = null
        if (this.pagination.prev_page_url) {
          prev = this.pagination.prev_page_url.split('=')
          prev = prev[1]
        }

        let next = null
        if (this.pagination.next_page_url) {
          next = this.pagination.next_page_url.split('=')
          next = next[1]
        }

        this.$set(this.pagination, 'previouspage', prev)
        this.$set(this.pagination, 'nextpage', next)
      }
      const handleError = () => {
        this.$set(this, 'loaded', true)

        EventBus.$emit('triggerAlertResponse', {
          status: 'error',
          message: 'Error getting resources.',
        })
      }

      this.$set(this, 'loaded', false)
      let submitData = {}

      // submitData.search = this.filterResourceBy;

      if (this.search_resources) {
        submitData.search = { keywords: this.search_resources }
      }
      if (submitData.search && this.types) {
        submitData.search = {
          keywords: this.search_resources,
          types: this.types,
        }
      }
      if (!submitData.search && this.types) {
        submitData.search = { types: this.types }
      }

      if (this.cloud) {
        // We need to attach the source_id into the resources we have on the local model...
      }

      // Pagination page?
      if (gotopage) {
        submitData.page = gotopage
      }

      if (this.isContributor) {
        const response = await this.dispatchGetResources(submitData)
        if (response) {
          handleSuccess(response)
        } else {
          handleError()
        }
      } else {
        // Get this post...
        axios
          .post('/api/auth/resources', submitData)
          .then((response) => handleSuccess(response))
          .catch(handleError)
      }
    },

    handleResourceClick(resource) {
      if (this.post) {
        // tiptap editor
        if (this.editorResources) {
          EventBus.$emit('assignResourceEditor', resource)
        } else if (this.image_files_only) {
          EventBus.$emit('assignResourceImage', resource)
        } else {
          EventBus.$emit('assignResource', resource)
        }
      } else {
        this.goToResource(resource.id)
      }
    },

    toggleResourceWindow() {
      EventBus.$emit('closeResourceModal')
    },

    resetSearch() {
      this.$set(this, 'resource_search', [])
      this.$set(this, 'resource_search_list', false)
    },
  },
}
</script>
